import { supabase } from './supabaseClient';
import { FormMetadata, FormResponse, User, FormVersion, Sector } from '@/types';

// --- MAPPERS ---

const mapUser = (row: any, userSectors?: any[]): User => {
    // Determine sectorIds from join table or fallback to legacy column
    let sectorIds: string[] = [];
    if (userSectors) {
        sectorIds = userSectors.filter(us => us.user_id === row.id).map(us => us.sector_id);
    } else {
        sectorIds = row.sector_id ? [row.sector_id] : [];
    }

    return {
        id: row.id,
        tenantId: row.tenant_id,
        name: row.name,
        role: row.role as 'admin' | 'operator',
        sectorIds,
        email: row.email,
        needsPasswordReset: row.needs_password_reset,
        avatarUrl: row.avatar_url,
        status: row.status as 'active' | 'inactive'
    };
};

const mapFormVersion = (row: any): FormVersion => ({
  version: row.version,
  schema: row.schema, // JSONB auto-parsed by Supabase JS
  createdAt: row.created_at,
  createdBy: row.created_by
});

const mapForm = (row: any, versions: FormVersion[] = []): FormMetadata => ({
  id: row.id,
  tenantId: row.tenant_id,
  title: row.title,
  description: row.description,
  sectorId: row.sector_id,
  sector: row.sectors?.name || row.sector || 'Geral',
  currentVersion: row.current_version,
  requireLocation: row.require_location || false,
  permitir_edicao: row.permitir_edicao || false,
  status: row.status as 'active' | 'archived',
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  versions: versions
});

const mapResponse = (row: any): FormResponse => ({
  id: row.id,
  tenantId: row.tenant_id,
  formId: row.form_id,
  formVersion: row.form_version,
  respondentId: row.respondent_id,
  respondentName: row.respondent_name,
  data: row.data,
  startedAt: row.started_at,
  submittedAt: row.submitted_at,
  status: row.status as 'draft' | 'submitted' | 'rejected' | 'pending',
  deviceInfo: row.device_info,
  securityMetadata: row.security_metadata
});

interface AuditLog {
    id: string;
    action: string;
    target: string;
    user: string;
    timestamp: string;
}

const mapLog = (row: any): AuditLog => ({
    id: row.id,
    action: row.action,
    target: row.target,
    user: row.user_name,
    timestamp: row.timestamp
});

class DatabaseService {

  // --- SECTORS ---

  async getSectors(): Promise<Sector[]> {
      const { data, error } = await supabase
          .from('sectors')
          .select('*')
          .order('name');

      if (error) {
          console.error("Error fetching sectors:", error);
          return [];
      }
      return data;
  }

  // --- AUTH ---

  async authenticate(email: string, password: string): Promise<User> {
    console.log(`[DB] authenticate called for: ${email}`);
    const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
    });

    if (error) {
        console.error("[DB] authenticate error:", error);
        throw error;
    }
    if (!data.user) throw new Error("Falha na autenticação");

    console.log(`[DB] authenticate success for user ID: ${data.user.id}, fetching profile...`);
    const user = await this.getProfile(data.user.id);
    if (!user) throw new Error("Perfil de usuário não encontrado. Se você acabou de se cadastrar, tente novamente em alguns instantes.");

    if (user.status === 'inactive') {
        await supabase.auth.signOut();
        throw new Error("Usuário bloqueado. Entre em contato com o administrador.");
    }

    await this.logAction('LOGIN_USUARIO', 'Sistema', user.name);
    return user;
  }

  async getCurrentUser(): Promise<User | null> {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      return this.getProfile(user.id);
  }

  async signOut(): Promise<void> {
      await supabase.auth.signOut();
  }

  async changeMyPassword(newPassword: string): Promise<void> {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) throw new Error("Não autenticado");

      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
      if (updateError) throw updateError;

      const { error: profileError } = await supabase.from('profiles').update({ needs_password_reset: false }).eq('id', user.id);
      if (profileError) throw profileError;
  }

  async getProfile(userId: string): Promise<User | null> {
      console.log(`[DB] getProfile called for: ${userId}`);
      const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .or(`id.eq.${userId},auth_id.eq.${userId}`)
          .maybeSingle();

      if (error) {
          console.error("[DB] getProfile error:", error);
          return null;
      }
      if (!data) {
          console.warn("[DB] getProfile: Profile not found for ID:", userId);
          return null;
      }

      const { data: userSectors } = await supabase
          .from('user_sectors')
          .select('*')
          .or(`user_id.eq.${userId},user_id.eq.${data.id}`);

      console.log(`[DB] getProfile success: ${data.name}`);
      return mapUser(data, userSectors || []);
  }

  // --- USER MANAGEMENT ---

  async getUsers(): Promise<User[]> {
      const { data, error } = await supabase.from('profiles').select('*');
      if (error) throw error;
      const { data: userSectors } = await supabase.from('user_sectors').select('*');
      return data.map(row => mapUser(row, userSectors || []));
  }

  // --- ADMIN ACTIONS (via Edge Function) ---

  private async callAdminEdgeFunction(action: string, payload: any, token: string) {
      const { data, error } = await supabase.functions.invoke('admin-users', {
          body: { action, payload },
          headers: { Authorization: `Bearer ${token}` }
      });

      if (error) {
          console.error("Edge Function Error:", error);
          throw new Error("Falha na operação administrativa.");
      }

      if (data.error) {
          throw new Error(data.error);
      }

      return data.data;
  }

  async createUser(userData: { name: string; role: 'admin' | 'operator'; sectorIds: string[]; email?: string; password?: string }, adminUser: User): Promise<void> {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error("Não autorizado");

      if (!userData.email || !userData.password) throw new Error("Email e senha obrigatórios");

      await this.callAdminEdgeFunction('create_user', {
          name: userData.name,
          role: userData.role,
          sectorIds: userData.sectorIds,
          email: userData.email,
          password: userData.password
      }, token);

      await this.logAction('CRIAR_USUARIO', userData.name, adminUser.name);
  }

  async updateUser(userId: string, updates: { name: string; email: string; password?: string; role: 'admin' | 'operator'; sectorIds: string[]; status: 'active' | 'inactive' }, adminUser: User): Promise<void> {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error("Não autorizado");

      await this.callAdminEdgeFunction('update_user', {
          targetUserId: userId,
          ...updates
      }, token);

      await this.logAction('EDITAR_USUARIO', updates.name, adminUser.name);
  }

  async resetPassword(userId: string, newPassword?: string, adminUser?: User): Promise<string> {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error("Não autorizado");

      if (!newPassword) throw new Error("Nova senha requerida");

      await this.callAdminEdgeFunction('reset_password', {
          targetUserId: userId,
          newPassword
      }, token);

      if (adminUser) {
          await this.logAction('REDEFINIR_SENHA', userId, adminUser.name);
      }

      return newPassword;
  }

  async updateUserStatus(userId: string, status: 'active' | 'inactive', adminUser: User): Promise<void> {
      const { error } = await supabase.from('profiles').update({ status }).eq('id', userId);
      if (error) throw error;
      await this.logAction(status === 'active' ? 'ATIVAR_USUARIO' : 'BLOQUEAR_USUARIO', userId, adminUser.name);
  }

  async updateUserRole(userId: string, role: 'admin' | 'operator', adminUser: User): Promise<void> {
      const { error } = await supabase.from('profiles').update({ role }).eq('id', userId);
      if (error) throw error;
      await this.logAction('ALTERAR_PAPEL', userId, adminUser.name);
  }

  // --- LOGS ---

  async getLogs(): Promise<AuditLog[]> {
      const { data, error } = await supabase
        .from('audit_logs')
        .select('*')
        .order('timestamp', { ascending: false });

      if (error) return [];
      return data.map(mapLog);
  }

  private async logAction(action: string, target: string, userName: string) {
      try {
        await supabase.from('audit_logs').insert({
            action,
            target,
            user_name: userName,
            timestamp: new Date().toISOString()
        });
      } catch (e) {
          console.error("Failed to log action:", e);
      }
  }

  // --- FORMS & RESPONSES ---

  async getForms(): Promise<FormMetadata[]> {
    const { data: formsData, error: formsError } = await supabase
        .from('forms')
        .select('*, sectors(name)')
        .order('created_at', { ascending: false });

    if (formsError) throw formsError;

    if (!formsData || formsData.length === 0) return [];

    const formIds = formsData.map(f => f.id);
    const { data: versionsData, error: versionsError } = await supabase
        .from('form_versions')
        .select('*')
        .in('form_id', formIds)
        .order('version', { ascending: true });

    if (versionsError) throw versionsError;

    return formsData.map(f => {
        const fVersions = versionsData.filter(v => v.form_id === f.id).map(mapFormVersion);
        return mapForm(f, fVersions);
    });
  }

  async getFormById(id: string): Promise<FormMetadata | undefined> {
      const { data: formData, error: formError } = await supabase
        .from('forms')
        .select('*, sectors(name)')
        .eq('id', id)
        .single();

      if (formError || !formData) return undefined;

      const { data: versionsData, error: versionsError } = await supabase
        .from('form_versions')
        .select('*')
        .eq('form_id', id)
        .order('version', { ascending: true });

      if (versionsError) throw versionsError;

      return mapForm(formData, versionsData.map(mapFormVersion));
  }

  async duplicateForm(formId: string, user: User): Promise<FormMetadata> {
      const original = await this.getFormById(formId);
      if (!original) throw new Error("Formulário não encontrado");

      const newId = `form-${Date.now()}`;
      const newForm = {
          id: newId,
          tenant_id: user.tenantId,
          title: `${original.title} (Cópia)`,
          description: original.description,
          sector_id: original.sectorId, // Copy ID
          sector: original.sector, // Copy Name (for legacy column)
          current_version: original.currentVersion,
          require_location: original.requireLocation,
          permitir_edicao: original.permitir_edicao,
          status: 'active',
          updated_at: new Date().toISOString()
      };

      const { error: insertError } = await supabase.from('forms').insert(newForm);
      if (insertError) throw insertError;

      // Copy versions
      const newVersions = original.versions.map(v => ({
          form_id: newId,
          version: v.version,
          schema: v.schema,
          created_at: v.createdAt,
          created_by: user.id
      }));

      const { error: versionError } = await supabase.from('form_versions').insert(newVersions);
      if (versionError) throw versionError;

      await this.logAction('DUPLICAR_FORMULARIO', newForm.title, user.name);

      const created = await this.getFormById(newId);
      if (!created) throw new Error("Failed to create copy");
      return created;
  }

  async saveForm(formMeta: Partial<FormMetadata>, schema: any[], user: User): Promise<FormMetadata> {

    // Check if updating existing
    if (formMeta.id) {
        const existing = await this.getFormById(formMeta.id);

        if (existing) {
            const newVersionNumber = existing.currentVersion + 1;

            // 1. Insert New Version
            const { error: vError } = await supabase.from('form_versions').insert({
                form_id: existing.id,
                version: newVersionNumber,
                schema: schema,
                created_by: user.id
            });
            if (vError) throw vError;

            // 2. Update Form Metadata
            const updatePayload: any = {
                    title: formMeta.title || existing.title,
                    description: formMeta.description || existing.description,
                    current_version: newVersionNumber,
                    require_location: formMeta.requireLocation !== undefined ? formMeta.requireLocation : existing.requireLocation,
                    permitir_edicao: formMeta.permitir_edicao !== undefined ? formMeta.permitir_edicao : existing.permitir_edicao,
                    updated_at: new Date().toISOString()
            };

            // Only update sector if provided
            if (formMeta.sectorId) {
                updatePayload.sector_id = formMeta.sectorId;
                if (formMeta.sector) updatePayload.sector = formMeta.sector;
            }

            const { data: updatedForm, error: fError } = await supabase
                .from('forms')
                .update(updatePayload)
                .eq('id', existing.id)
                .eq('current_version', existing.currentVersion)
                .select('*, sectors(name)')
                .single();

            if (fError) {
                if (fError.code === 'PGRST116') {
                    throw new Error("Conflito de versão: O formulário foi modificado por outro usuário. Por favor, recarregue e tente novamente.");
                }
                throw fError;
            }

            await this.logAction('ATUALIZAR_FORMULARIO', updatedForm.title, user.name);
            const final = await this.getFormById(existing.id);
            return final!;
        }
    }

    // Create New Form
    const newId = `form-${Date.now()}`;
    const newForm = {
        id: newId,
        tenant_id: user.tenantId,
        title: formMeta.title || 'Formulário Sem Título',
        description: formMeta.description || '',
        sector_id: formMeta.sectorId || 'geral', // Ensure we have ID
        sector: formMeta.sector || 'Geral', // Legacy
        current_version: 1,
        require_location: formMeta.requireLocation || false,
        permitir_edicao: formMeta.permitir_edicao || false,
        status: 'active'
    };

    const { error: createError } = await supabase.from('forms').insert(newForm);
    if (createError) throw createError;

    const { error: vError } = await supabase.from('form_versions').insert({
        form_id: newId,
        version: 1,
        schema: schema,
        created_by: user.id
    });
    if (vError) throw vError;

    await this.logAction('CRIAR_FORMULARIO', newForm.title, user.name);
    const final = await this.getFormById(newId);
    return final!;
  }

  // --- RESPONSES ---

  async submitResponse(
    response: Omit<FormResponse, 'id' | 'submittedAt' | 'tenantId'>,
    user: User
  ): Promise<FormResponse> {
    const newId = `res-${Date.now()}`;

    // Helper: Upload base64 to storage and return public url
    const uploadBase64ToStorage = async (base64Str: string, fieldId: string): Promise<string> => {
        // If it's already a URL, return it
        if (base64Str.startsWith('http')) return base64Str;

        try {
            // Extract content type and base64 data
            const matches = base64Str.match(/^data:([A-Za-z-+/]+);base64,(.+)$/);
            if (!matches || matches.length !== 3) return base64Str;

            const contentType = matches[1];
            const b64Data = matches[2];

            // Convert base64 to blob
            const byteCharacters = atob(b64Data);
            const byteArrays = [];
            for (let offset = 0; offset < byteCharacters.length; offset += 512) {
                const slice = byteCharacters.slice(offset, offset + 512);
                const byteNumbers = new Array(slice.length);
                for (let i = 0; i < slice.length; i++) {
                    byteNumbers[i] = slice.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                byteArrays.push(byteArray);
            }
            const blob = new Blob(byteArrays, { type: contentType });

            // Generate unique filename
            const ext = contentType.split('/')[1] || 'jpeg';
            const fileName = `${newId}_${fieldId}_${Date.now()}.${ext}`;

            // Upload to Supabase Storage
            const { data, error } = await supabase.storage
                .from('forms_media')
                .upload(`${user.tenantId}/${fileName}`, blob, {
                    contentType,
                    upsert: false
                });

            if (error) throw error;

            // Get public URL
            const { data: { publicUrl } } = supabase.storage
                .from('forms_media')
                .getPublicUrl(`${user.tenantId}/${fileName}`);

            return publicUrl;
        } catch (e) {
            console.error('[DB] Error uploading media to storage:', e);
            return base64Str; // Fallback to base64 if upload fails
        }
    };

    // Extract deviceInfo to separate column
    const { deviceInfo, securityMetadata, ...restData } = response;

    // Convert all base64 data to storage URLs
    const processedData = { ...restData.data };

    for (const fieldId of Object.keys(processedData)) {
        const field = processedData[fieldId];

        if (field && field.value) {
            if (typeof field.value === 'string' && field.value.startsWith('data:image')) {
                // Single base64 image
                field.value = await uploadBase64ToStorage(field.value, fieldId);
            } else if (Array.isArray(field.value)) {
                // Array of items (could be base64 images)
                const processedArray = [];
                for (let i = 0; i < field.value.length; i++) {
                    const item = field.value[i];
                    if (typeof item === 'string' && item.startsWith('data:image')) {
                        processedArray.push(await uploadBase64ToStorage(item, `${fieldId}_${i}`));
                    } else {
                        processedArray.push(item);
                    }
                }
                field.value = processedArray;
            }
        }
    }

    restData.data = processedData;

    const dbRow = {
        id: newId,
        tenant_id: user.tenantId,
        form_id: response.formId,
        form_version: response.formVersion,
        respondent_id: user.id,
        respondent_name: user.name,
        data: response.data,
        device_info: deviceInfo, // Mapped to device_info column
        security_metadata: securityMetadata,
        started_at: response.startedAt,
        submitted_at: new Date().toISOString(),
        status: response.status || 'submitted'
    };

    const { data, error } = await supabase
        .from('responses')
        .insert(dbRow)
        .select()
        .single();

    if (error) throw error;

    await this.logAction('ENVIAR_RESPOSTA', `Formulário ${response.formId}`, user.name);
    return mapResponse(data);
  }

  async updateResponse(responseId: string, updatedData: Record<string, any>, user: User): Promise<FormResponse> {
      const { data, error } = await supabase
          .from('responses')
          .update({ data: updatedData })
          .eq('id', responseId)
          .select()
          .single();

      if (error) throw error;
      
      await this.logAction('EDITAR_RESPOSTA', `Resposta ${responseId}`, user.name);
      return mapResponse(data);
  }

  // Optimize: Added limit and default pagination
  async getResponses(formId?: string, limit: number = 50): Promise<FormResponse[]> {
    let query = supabase.from('responses').select('*');

    if (formId) {
        query = query.eq('form_id', formId);
    }

    const { data, error } = await query
        .order('submitted_at', { ascending: false })
        .limit(limit);

    if (error) throw error;

    return data.map(mapResponse);
  }

  // NEW: Lightweight fetch for Dashboard (No Data/Base64)
  async getResponseSummaries(limit: number = 50): Promise<FormResponse[]> {
      const { data, error } = await supabase
          .from('responses')
          .select('id, tenant_id, form_id, form_version, respondent_id, respondent_name, started_at, submitted_at, status')
          .order('submitted_at', { ascending: false })
          .limit(limit);

      if (error) throw error;

      return data.map(row => ({
          ...mapResponse(row),
          data: {} // Empty data to prevent memory issues
      }));
  }

  // NEW: Stats for Dashboard
  async getDashboardStats() {
      const today = new Date();
      today.setHours(0,0,0,0);
      const todayIso = today.toISOString();

      // 1. Responses Today
      const { count: responsesToday } = await supabase
          .from('responses')
          .select('*', { count: 'exact', head: true })
          .gte('submitted_at', todayIso);

      // 2. Active Forms
      const { count: activeForms } = await supabase
          .from('forms')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'active');

      return {
          responsesToday: responsesToday || 0,
          activeForms: activeForms || 0,
          refusalRate: 0, // Placeholder
          avgTime: 'N/A'
      };
  }

  async getUserResponses(userId: string): Promise<FormResponse[]> {
      const { data, error } = await supabase
          .from('responses')
          .select('*')
          .eq('respondent_id', userId)
          .order('submitted_at', { ascending: false });

      if (error) throw error;
      return data.map(mapResponse);
  }

  async getResponseById(id: string): Promise<FormResponse | null> {
      const { data, error } = await supabase
          .from('responses')
          .select('*')
          .eq('id', id)
          .single();

      if (error) {
          console.error("Error fetching response:", error);
          return null;
      }
      return mapResponse(data);
  }

  // --- NEW: SERVER-SIDE FILTERING & EXPORT ---
  async searchResponsesFromServer(queryText: string): Promise<FormResponse[]> {
      const { data, error } = await supabase
          .from('responses')
          .select('*')
          .or(`respondent_name.ilike.%${queryText}%,data::text.ilike.%${queryText}%`)
          .order('submitted_at', { ascending: false })
          .limit(100);

      if (error) {
          console.error("Erro na busca de servidor:", error);
          throw error;
      }
      return data.map(mapResponse);
  }

  async getAllResponsesForExport(filters: any): Promise<FormResponse[]> {
      let allRows: any[] = [];
      let from = 0;
      const step = 1000;
      const MAX_ROWS = 50000;

      while (allRows.length < MAX_ROWS) {
          let query = supabase.from('responses').select('*');
          
          if (filters.forms && filters.forms.length > 0) {
              query = query.in('form_id', filters.forms);
          }
          if (filters.respondent && filters.respondent !== 'all') {
              query = query.eq('respondent_id', filters.respondent);
          }
          if (filters.startDate) {
              query = query.gte('started_at', filters.startDate);
          }
          if (filters.endDate) {
              const end = new Date(filters.endDate);
              end.setDate(end.getDate() + 1);
              query = query.lt('started_at', end.toISOString());
          }
          
          const { data, error } = await query
              .order('submitted_at', { ascending: false })
              .range(from, from + step - 1);
          
          if (error) throw error;
          if (!data || data.length === 0) break;
          
          allRows.push(...data);
          
          if (data.length < step) break;
          from += step;
      }

      return allRows.map(mapResponse);
  }


  // --- LOGISTICS & ANALYTICS ---

  async getLogisticsData(): Promise<{ responses: FormResponse[], forms: FormMetadata[], versions: FormVersion[] }> {
      console.log(`[DB] getLogisticsData called`);

      let materializedResponses: FormResponse[] = [];
      const FORM_VERSION = 1;

      // The 3 Virtual Forms
      const forms: FormMetadata[] = [
        {
          id: 'form-1775766905782',
          title: "Abastecimento",
          description: "View: mv_abastecimento",
          status: "active", sectorId: "externo", sector: "Externo",
          currentVersion: FORM_VERSION, requireLocation: true,
          versions: [],
          createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), tenantId: ''
        },
        {
          id: 'form-1775762620276',
          title: "Chegada no Cliente",
          description: "View: mv_chegada_cliente",
          status: "active", sectorId: "externo", sector: "Externo",
          currentVersion: FORM_VERSION, requireLocation: true,
          versions: [],
          createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), tenantId: ''
        },
        {
          id: 'vw-lavagens-cestos',
          title: "Lavagens pela Balança",
          description: "View: mv_lavagens_cestos",
          status: "active", sectorId: "externo", sector: "Externo",
          currentVersion: FORM_VERSION, requireLocation: false,
          versions: [],
          createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), tenantId: ''
        },
        {
          id: 'vw-coletas-entregas',
          title: "IdTrack Coleta e Entrega",
          description: "View: analytics.coletas_entregas_resumo",
          status: "active", sectorId: "externo", sector: "Externo",
          currentVersion: FORM_VERSION, requireLocation: false,
          versions: [],
          createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), tenantId: ''
        },
        {
          id: 'vw-rol-detalhado',
          title: "Rol (Completo)",
          description: "View: vw_rol_detalhado",
          status: "active", sectorId: "externo", sector: "Externo",
          currentVersion: FORM_VERSION, requireLocation: false,
          versions: [],
          createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), tenantId: ''
        }
      ];

      const versions: FormVersion[] = [
        {
          id: `ver-form-1775766905782`,
          formId: 'form-1775766905782',
          version: FORM_VERSION,
          createdAt: new Date().toISOString(),
          createdBy: 'system',
          schema: [
            { id: "veiculo", type: "text_short", label: "Veículo", required: false },
            { id: "posto_combustivel", type: "text_short", label: "Posto", required: false },
            { id: "tipo_combustivel", type: "text_short", label: "Combustível", required: false },
            { id: "hodometro", type: "number", label: "Hodômetro", required: false },
            { id: "litros_abastecidos", type: "number", label: "Litros", required: false },
            { id: "valor_total_pago", type: "number", label: "Valor Total", required: false },
            { id: "observacao", type: "text_short", label: "Observação", required: false },
          ]
        } as any,
        {
          id: `ver-form-1775762620276`,
          formId: 'form-1775762620276',
          version: FORM_VERSION,
          createdAt: new Date().toISOString(),
          createdBy: 'system',
          schema: [
            { id: "veiculo", type: "text_short", label: "Veículo", required: false },
            { id: "cliente", type: "text_short", label: "Cliente", required: false },
            { id: "hodometro", type: "number", label: "Hodômetro", required: false },
            { id: "observacao", type: "text_short", label: "Observação", required: false },
          ]
        } as any,
        {
          id: `ver-vw-lavagens-cestos`,
          formId: 'vw-lavagens-cestos',
          version: FORM_VERSION,
          createdAt: new Date().toISOString(),
          createdBy: 'system',
          schema: [
            { id: "id_lavagem_agrupada", type: "text_short", label: "ID Lavagem", required: false },
            { id: "maquina_numero", type: "number", label: "Máquina", required: false },
            { id: "cesto_numero", type: "text_short", label: "Cesto Nº", required: false },
            { id: "kg", type: "number", label: "KG", required: false },
            { id: "cliente", type: "text_short", label: "Cliente", required: false },
            { id: "categoria", type: "text_short", label: "Categoria", required: false },
          ]
        } as any,
        {
          id: `ver-vw-coletas-entregas`,
          formId: 'vw-coletas-entregas',
          version: FORM_VERSION,
          createdAt: new Date().toISOString(),
          createdBy: 'system',
          schema: [
            { id: "cliente", type: "text_short", label: "Cliente", required: false },
            { id: "os_coleta", type: "number", label: "OS Coleta", required: false },
            { id: "os_entrega", type: "number", label: "OS Entrega", required: false },
            { id: "descricao_os", type: "text_short", label: "Descrição OS", required: false },
            { id: "peso_sujo", type: "number", label: "Peso Sujo", required: false },
            { id: "peso_limpo", type: "number", label: "Peso Limpo", required: false },
            { id: "coleta_rfid", type: "number", label: "Coleta RFID", required: false },
            { id: "entrega_rfid", type: "number", label: "Entrega RFID", required: false },
            { id: "contagem_coleta", type: "number", label: "Contagem Coleta", required: false },
            { id: "contagem_entrega", type: "number", label: "Contagem Entrega", required: false },
            { id: "tipo_os_coleta", type: "text_short", label: "Tipo OS Coleta", required: false },
            { id: "tipo_os_entrega", type: "text_short", label: "Tipo OS Entrega", required: false },
            { id: "rol", type: "text_short", label: "Rol", required: false },
            { id: "data_coleta", type: "date", label: "Data Coleta", required: false },
            { id: "data_entrega", type: "date", label: "Data Entrega", required: false }
          ]
        } as any,
        {
          id: `ver-vw-rol-detalhado`,
          formId: 'vw-rol-detalhado',
          version: FORM_VERSION,
          createdAt: new Date().toISOString(),
          createdBy: 'system',
          schema: [
            { id: "rol_id", type: "number", label: "Rol ID", required: false },
            { id: "response_id", type: "text_short", label: "Response ID", required: false },
            { id: "numero_rol", type: "text_short", label: "Número Rol", required: false },
            { id: "data_rol", type: "date", label: "Data Rol", required: false },
            { id: "cliente", type: "text_short", label: "Cliente", required: false },
            { id: "relave", type: "text_short", label: "Relave", required: false },
            { id: "dom_id", type: "number", label: "Dom ID", required: false },
            { id: "peca", type: "text_short", label: "Peça", required: false },
            { id: "quantidade", type: "number", label: "Quantidade", required: false },
            { id: "tipo", type: "text_short", label: "Tipo", required: false },
          ]
        } as any
      ];

      // Attach versions back to forms
      forms[0].versions = [versions[0]];
      forms[1].versions = [versions[1]];
      forms[2].versions = [versions[2]];
      forms[3].versions = [versions[3]];
      forms[4].versions = [versions[4]];

      try {
        const fetchAll = async (tableName: string, orderField: string) => {
            let allRows: any[] = [];
            let from = 0;
            const step = 1000;
            const MAX_ROWS = 50000;

            while (allRows.length < MAX_ROWS) {
                const { data, error } = await supabase
                    .from(tableName)
                    .select('*')
                    .order(orderField, { ascending: false })
                    .range(from, from + step - 1);
                
                if (error) {
                    console.error(`[DB] Erro paginando ${tableName}:`, error);
                    break;
                }
                
                if (!data || data.length === 0) break;
                
                allRows.push(...data);
                
                if (data.length < step) break;
                from += step;
            }
            return allRows;
        };

        const [mvAbastecimento, mvChegada, mvLavagens, vwRolDetalhado] = await Promise.all([
            fetchAll('mv_abastecimento', 'submitted_at'),
            fetchAll('mv_chegada_cliente', 'submitted_at'),
            fetchAll('mv_lavagens_cestos', 'data'),
            fetchAll('vw_rol_detalhado', 'rol_id')
        ]);

        let vwColetas = null;
        try {
            const coletasRes = await fetch('/api/coletas_entregas');
            if (coletasRes.ok) vwColetas = await coletasRes.json();
        } catch (e) {
            console.error('[DB] Erro fetch coletas_entregas:', e);
        }

        if (mvAbastecimento) {
            mvAbastecimento.forEach((row: any) => {
                materializedResponses.push({
                    id: row.response_id,
                    tenantId: '',
                    formId: 'form-1775766905782',
                    formVersion: FORM_VERSION,
                    respondentId: row.respondent_id,
                    respondentName: row.respondent_name,
                    startedAt: row.submitted_at,
                    submittedAt: row.submitted_at,
                    status: 'submitted',
                    deviceInfo: row.latitude ? { geolocation: { lat: Number(row.latitude), lng: Number(row.longitude) } } : undefined,
                    data: {
                        veiculo: { value: row.veiculo },
                        posto_combustivel: { value: row.posto_combustivel },
                        tipo_combustivel: { value: row.tipo_combustivel },
                        hodometro: { value: Number(row.hodometro) },
                        litros_abastecidos: { value: Number(row.litros_abastecidos) },
                        valor_total_pago: { value: Number(row.valor_total_pago) },
                        observacao: { value: row.observacao },
                        latitude: { value: row.latitude },
                        longitude: { value: row.longitude }
                    }
                } as FormResponse);
            });
        }

        if (mvChegada) {
            mvChegada.forEach((row: any) => {
                materializedResponses.push({
                    id: row.response_id,
                    tenantId: '',
                    formId: 'form-1775762620276',
                    formVersion: FORM_VERSION,
                    respondentId: row.respondent_id,
                    respondentName: row.respondent_name,
                    startedAt: row.submitted_at,
                    submittedAt: row.submitted_at,
                    status: 'submitted',
                    deviceInfo: row.latitude ? { geolocation: { lat: Number(row.latitude), lng: Number(row.longitude) } } : undefined,
                    data: {
                        veiculo: { value: row.veiculo },
                        cliente: { value: row.cliente },
                        hodometro: { value: Number(row.hodometro) },
                        observacao: { value: row.observacao },
                        latitude: { value: row.latitude },
                        longitude: { value: row.longitude }
                    }
                } as FormResponse);
            });
        }

        if (mvLavagens) {
            mvLavagens.forEach((row: any, i: number) => {
                materializedResponses.push({
                    id: row.id_operacao_cesto || `lavagem-${i}`,
                    tenantId: '',
                    formId: 'vw-lavagens-cestos',
                    formVersion: FORM_VERSION,
                    respondentId: 'system',
                    respondentName: 'Sistema',
                    startedAt: row.data,
                    submittedAt: row.data,
                    status: 'submitted',
                    data: {
                        id_lavagem_agrupada: { value: row.id_lavagem_agrupada },
                        maquina_numero: { value: Number(row.maquina_numero) },
                        cesto_numero: { value: row.cesto_numero },
                        kg: { value: Number(row.kg) },
                        cliente: { value: row.cliente },
                        categoria: { value: row.categoria }
                    }
                } as FormResponse);
            });
        }

        if (vwColetas) {
            const parseIdTrackDate = (d: any): string | null => {
                if (!d) return null;
                const str = String(d).trim();
                if (str.includes('/')) {
                    const parts = str.split(' ');
                    const dateParts = parts[0].split('/');
                    if (dateParts.length === 3) {
                        const timePart = parts[1] || '00:00:00';
                        const isoStr = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}T${timePart}.000Z`;
                        const dateObj = new Date(isoStr);
                        if (!isNaN(dateObj.getTime())) return dateObj.toISOString();
                    }
                } else if (str.includes('-')) {
                    const isoStr = str.replace(' ', 'T') + (str.includes('Z') ? '' : 'Z');
                    const dateObj = new Date(isoStr);
                    if (!isNaN(dateObj.getTime())) return dateObj.toISOString();
                }
                return null;
            };

            const safeNumber = (val: any): number | null => {
                if (val === undefined || val === null || val === '') return null;
                const n = Number(val);
                return isNaN(n) ? null : n;
            };

            vwColetas.forEach((row: any, i: number) => {
                const dataColeta = parseIdTrackDate(row["Data Coleta"]);
                const dataEntrega = parseIdTrackDate(row["Data Entrega"]);
                const submittedAt = dataColeta || dataEntrega || new Date().toISOString();

                materializedResponses.push({
                    id: `coleta-${i}-${Date.now()}`,
                    tenantId: '',
                    formId: 'vw-coletas-entregas',
                    formVersion: FORM_VERSION,
                    respondentId: 'system',
                    respondentName: 'Sistema (IdTrack)',
                    startedAt: submittedAt,
                    submittedAt: submittedAt,
                    status: 'submitted',
                    data: {
                        cliente: { value: row["Cliente"] || "" },
                        os_coleta: { value: safeNumber(row["OS Coleta"]) },
                        os_entrega: { value: safeNumber(row["OS Entrega"]) },
                        descricao_os: { value: row["Descrição OS"] || "" },
                        peso_sujo: { value: safeNumber(row["Peso Sujo"]) },
                        peso_limpo: { value: safeNumber(row["Peso Limpo"]) },
                        coleta_rfid: { value: safeNumber(row["Coleta RFID"]) },
                        entrega_rfid: { value: safeNumber(row["Entrega RFID"]) },
                        contagem_coleta: { value: safeNumber(row["Contagem Coleta"]) },
                        contagem_entrega: { value: safeNumber(row["Contagem Entrega"]) },
                        tipo_os_coleta: { value: row["Tipo OS Coleta"] || "" },
                        tipo_os_entrega: { value: row["Tipo OS Entrega"] || "" },
                        rol: { value: row["Rol"] || "" },
                        data_coleta: { value: dataColeta },
                        data_entrega: { value: dataEntrega }
                    }
                } as FormResponse);
            });
        }

        if (vwRolDetalhado) {
            vwRolDetalhado.forEach((row: any, i: number) => {
                const submittedAt = row.data_rol ? new Date(row.data_rol).toISOString() : new Date().toISOString();
                materializedResponses.push({
                    id: row.response_id ? `${row.response_id}-${row.dom_id || i}` : `rol-${i}`,
                    tenantId: '',
                    formId: 'vw-rol-detalhado',
                    formVersion: FORM_VERSION,
                    respondentId: 'system',
                    respondentName: 'Sistema',
                    startedAt: submittedAt,
                    submittedAt: submittedAt,
                    status: 'submitted',
                    data: {
                        rol_id: { value: Number(row.rol_id) },
                        response_id: { value: row.response_id },
                        numero_rol: { value: row.numero_rol },
                        data_rol: { value: row.data_rol },
                        cliente: { value: row.cliente },
                        relave: { value: row.relave },
                        dom_id: { value: Number(row.dom_id) },
                        peca: { value: row.peca },
                        quantidade: { value: Number(row.quantidade) },
                        tipo: { value: row.tipo }
                    }
                } as FormResponse);
            });
        }

      } catch (err) {
        console.error('[DB] Failed to fetch materialized views:', err);
      }

      return {
          responses: materializedResponses,
          forms,
          versions
      };
  }

}
export const db = new DatabaseService();
